import { Navigate, Outlet, RouteObject } from "react-router-dom";

import Error404 from "@/components/errors/Error404";
import {
  AuthenticationGuard,
  Layout,
  LayoutMinimal,
} from "@/components/layout";
import { WorkspaceChecker } from "@/components/WorkspaceChecker";

import { RootLayout } from "./RootLayout";
import { StylesToModelsRedirect } from "./styles/redirectToModels";

export const routes = [
  // logged out routes, Note: should probably not be child of UrqlProvider that try to authenticate the user
  {
    path: "login",
    lazy: () => import("./login"),
  },
  {
    path: "logout",
    lazy: () => import("./logout"),
  },
  {
    element: <LayoutMinimal />,
    children: [
      {
        path: "unverified",
        lazy: () => import("./unverified"),
      },
      {
        path: "error",
        lazy: () => import("./error"),
      },
    ],
  },
  // logged in routes
  {
    element: <AuthenticationGuard />,
    children: [
      {
        element: <LayoutMinimal />,
        children: [
          {
            path: "tos",
            lazy: () => import("./tos"),
          },
        ],
      },
      // logged in and valid account routes
      {
        element: <RootLayout />,
        children: [
          {
            index: true,
            lazy: () => import("./index/index"),
          },
          {
            path: "new",
            lazy: () => import("./new"),
          },
          {
            path: "settings",
            children: [
              {
                index: true,
                lazy: () => import("./user/settings"),
              },
              {
                path: "general",
                lazy: () => import("./user/settings/general"),
              },
            ],
          },
          {
            path: ":workspace",
            element: <WorkspaceChecker />,
            children: [
              {
                lazy: () => import("./workspace/layout"),
                children: [
                  {
                    index: true,
                    lazy: () => import("./workspace"),
                  },
                  {
                    path: "drive/*",
                    lazy: () => import("./workspace/drive"),
                  },
                  {
                    path: "activity",
                    lazy: () => import("./activity/layout"),
                    children: [
                      {
                        index: true,
                        lazy: () => import("./activity"),
                      },
                      {
                        path: "generations",
                        lazy: () => import("./activity/generations"),
                      },
                      {
                        path: "events",
                        lazy: () => import("./activity/events"),
                      },
                    ],
                  },
                  {
                    path: "workflows",
                    element: <Outlet />,
                    children: [
                      {
                        index: true,
                        lazy: () => import("./blueprints/list"),
                      },
                      {
                        path: ":blueprintId",
                        lazy: () => import("./blueprints/detailsRoot"),
                        children: [
                          {
                            index: true,
                            lazy: () => import("./blueprints/edit"),
                          },
                          {
                            path: "edit",
                            lazy: () => import("./blueprints/edit"),
                          },
                          {
                            path: "edit/:blueprintVersionId",
                            lazy: () => import("./blueprints/edit"),
                          },
                        ],
                      },
                      {
                        path: "debug",
                        children: [
                          {
                            path: "new",
                            lazy: () => import("./blueprints/debugger/new"),
                          },
                          {
                            path: ":versionId/edit",
                            lazy: () => import("./blueprints/debugger/edit"),
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: "session",
                    children: [
                      {
                        path: "new",
                        lazy: () => import("./workspace/session/new"),
                      },
                      {
                        path: ":sessionId",
                        lazy: () => import("./workspace/session/sessionLayout"),
                        children: [
                          {
                            index: true,
                            lazy: () => import("./workspace/session/sessionId"),
                          },
                          {
                            path: "asset",
                            children: [
                              {
                                path: ":assetId",
                                children: [
                                  {
                                    index: true,
                                    lazy: () =>
                                      import("./workspace/session/sessionId"), // Note: same page to handle sessionId and assetId? Maybe we should create a new page for assets instead of 1 page do all.
                                  },
                                ],
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: "assets",
                    children: [
                      {
                        path: ":assetId",
                        children: [
                          {
                            index: true,
                            lazy: () => import("./workspace/asset"),
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: "models",
                    children: [
                      {
                        index: true,
                        lazy: () => import("./models/list"),
                      },
                      {
                        path: "create",
                        children: [
                          {
                            index: true,
                            lazy: () => import("./styles/create"),
                          },
                        ],
                      },
                      {
                        path: ":modality/:type/:subType?",
                        element: <Navigate to=".." replace />,
                      },
                      {
                        path: ":slug",
                        lazy: () => import("./styles/layout"),
                        children: [
                          {
                            index: true,
                            lazy: () => import("./styles/overview"),
                          },
                          {
                            path: "create",
                            lazy: () => import("./styles/create"),
                          },
                          {
                            path: "runs",
                            lazy: () => import("./styles/runs"),
                          },
                          {
                            path: "versions",
                            lazy: () => import("./styles/versions"),
                          },
                          {
                            path: "analyze",
                            lazy: () => import("./styles/analyze"),
                          },
                          {
                            path: "edit",
                            lazy: () => import("./styles/edit"),
                          },
                          // Legacy routes redirecting to new equivalents
                          {
                            path: "variations",
                            lazy: () => import("./styles/versions"),
                          },
                          {
                            path: "settings",
                            lazy: () => import("./styles/edit"),
                          },
                        ],
                      },
                    ],
                  },
                  // Legacy /styles routes - redirect to /models
                  {
                    path: "styles/*",
                    element: <StylesToModelsRedirect />,
                  },
                  {
                    path: "settings",
                    children: [
                      {
                        lazy: () => import("./workspace/settings/layout"),
                        children: [
                          {
                            index: true,
                            lazy: () => import("./workspace/settings"),
                          },
                          {
                            path: "general",
                            lazy: () => import("./workspace/settings/general"),
                          },
                          {
                            path: "members",
                            lazy: () => import("./workspace/settings/members"),
                          },
                          {
                            path: "labels",
                            lazy: () => import("./workspace/settings/labels"),
                          },
                          {
                            path: "usage",
                            children: [
                              {
                                index: true,
                                lazy: () =>
                                  import("./workspace/settings/usage"),
                              },
                              {
                                path: "subscription",
                                lazy: () =>
                                  import(
                                    "./workspace/settings/usage/subscription"
                                  ),
                              },
                              {
                                path: "one-time",
                                lazy: () =>
                                  import("./workspace/settings/usage/one-time"),
                              },
                            ],
                          },
                          {
                            path: "notices",
                            lazy: () => import("./workspace/settings/notices"),
                          },
                          {
                            path: "groups",
                            lazy: () => import("./workspace/settings/groups"),
                          },
                          {
                            path: "usage-limits",
                            lazy: () =>
                              import("./workspace/settings/usage-limits"),
                          },
                          {
                            path: "models",
                            lazy: () => import("./workspace/settings/models"),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: "sharing",
            children: [
              {
                index: true,
                lazy: () => import("./sharing"),
              },
              {
                path: "workflows/:blueprintId",
                lazy: () => import("./sharing"),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <Layout />,
    children: [
      {
        path: "*",
        element: <Error404 />, // TODO: Make it a page
      },
    ],
  },
] satisfies RouteObject[];
